/*
 * Iter XX.41 — Founder Teams overview (mounted in WebFounderDashboard).
 * Read-only KPI tiles + full teams list.
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";

export function FounderTeamsSection() {
  const [overview, setOverview] = useState(null);
  const [teams, setTeams] = useState(null);
  const [err, setErr] = useState(null);
  const [rev, setRev] = useState(0);
  const [showTest, setShowTest] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data: o }, { data: t }] = await Promise.all([
          api.get("/admin/teams/overview"),
          api.get("/admin/teams"),
        ]);
        if (!alive) return;
        setOverview(o); setTeams(t.teams || []); setErr(null);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "load_failed");
      }
    })();
    return () => { alive = false; };
  }, [rev]);

  return (
    <section data-testid="section-founder-teams" className="space-y-4">
      <div className="mb-1 flex items-start gap-4">
        <div className="flex-1">
          <p className="label-eyebrow">teams · distribution engine</p>
          <p className="text-xs text-neutral-500 mt-0.5">
            one coach → one invite → 50 players · gully for teams · phase 1
          </p>
        </div>
        <button
          onClick={() => setShowTest(true)}
          className="text-xs px-3 py-1.5 rounded border border-neutral-700 text-neutral-300 hover:border-neutral-400"
          data-testid="founder-teams-create-test-btn"
        >
          + create test team
        </button>
      </div>

      {showTest && (
        <CreateTestTeamModal
          onClose={() => setShowTest(false)}
          onCreated={() => { setShowTest(false); setRev((r) => r + 1); }}
        />
      )}

      {err && <div className="surface-1 p-4 text-red-400 text-sm">{err}</div>}
      {!overview && !err && <div className="surface-1 p-4 text-neutral-500 text-sm">Loading…</div>}

      {overview && (
        <div className="surface-1 p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="founder-teams-kpis">
          <Kpi label="teams"                v={overview.teams} testid="ft-teams" />
          <Kpi label="players"              v={overview.players} testid="ft-players" />
          <Kpi label="weekly active"        v={overview.weekly_active_players} testid="ft-wau" />
          <Kpi label="invites sent"         v={overview.invites_sent} testid="ft-invites-sent" />
          <Kpi label="invites accepted"     v={overview.invites_accepted} testid="ft-invites-accepted" />
          <Kpi label="activation"           v={`${Math.round((overview.activation_rate || 0) * 100)}%`} testid="ft-activation" />
          <Kpi
            label="most active"
            v={overview.most_active_team
              ? `${overview.most_active_team.name} · ${overview.most_active_team.active_players_7d}`
              : "—"}
            testid="ft-most-active"
          />
        </div>
      )}

      {teams && teams.length > 0 && (
        <div className="surface-1 p-4" data-testid="founder-teams-list">
          <div className="label-eyebrow mb-3">all teams</div>
          <div className="space-y-2">
            {teams.map((t) => (
              <div
                key={t.team_id}
                className="grid grid-cols-6 gap-2 text-xs items-center border-b border-neutral-900 pb-2"
                data-testid={`ft-row-${t.team_id}`}
              >
                <div className="col-span-2 text-neutral-200">{t.name}</div>
                <div className="text-neutral-500">{t.entity_type}</div>
                <div className="text-neutral-500">{[t.city, t.country].filter(Boolean).join(", ")}</div>
                <div className="text-neutral-500">{t.coach_name}</div>
                <div className="text-right">
                  <span className="text-neutral-300 tabular-nums">{t.player_count || 0}</span>
                  <span className="text-neutral-600"> players</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {teams && teams.length === 0 && (
        <div className="surface-1 p-6 text-neutral-500 text-sm" data-testid="founder-teams-empty">
          No teams yet. Convert a Growth opportunity below to create your first one.
        </div>
      )}
    </section>
  );
}

function Kpi({ label, v, testid }) {
  return (
    <div data-testid={testid}>
      <div className="label-eyebrow">{label}</div>
      <div className="font-display text-xl font-light tabular-nums">{v ?? "—"}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function CreateTestTeamModal({ onClose, onCreated }) {
  const [name, setName] = useState("Test Cricket Academy");
  const [coachName, setCoachName] = useState("Test Coach");
  const [coachEmail, setCoachEmail] = useState("");
  const [entityType, setEntityType] = useState("academy");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function create() {
    if (!name.trim() || !coachName.trim() || !coachEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/teams/create-test", {
        team_name: name.trim(),
        coach_name: coachName.trim(),
        coach_email: coachEmail.trim(),
        entity_type: entityType,
        city: city.trim() || null,
        country: country.trim() || null,
      });
      setResult(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  }

  function done() {
    setResult(null);
    onCreated();
  }

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      data-testid="create-test-team-modal"
    >
      <div className="bg-neutral-950 border border-neutral-800 rounded-lg p-6 max-w-md w-full space-y-4">
        <div className="flex items-center justify-between">
          <p className="label-eyebrow">create test team · smoke test</p>
          {!busy && !result && (
            <button
              onClick={onClose}
              className="text-neutral-500 hover:text-neutral-200 text-lg leading-none"
              data-testid="create-test-team-close"
            >×</button>
          )}
        </div>

        {result ? (
          <div className="space-y-3 text-sm" data-testid="create-test-team-result">
            <p className="text-emerald-400">
              ✓ Team created: <span className="text-neutral-100">{result.team.name}</span>
            </p>
            <div>
              <div className="text-xs text-neutral-500 mb-1">Player invite link</div>
              <a
                href={result.team.invite_url}
                target="_blank" rel="noreferrer"
                className="block font-mono text-xs bg-neutral-900 rounded px-3 py-2 break-all underline text-neutral-200"
                data-testid="create-test-team-invite-url"
              >{result.team.invite_url}</a>
            </div>
            <div>
              <div className="text-xs text-neutral-500 mb-1">Invite code</div>
              <div className="font-mono text-lg tracking-[0.2em] bg-neutral-900 rounded px-3 py-2 text-neutral-200"
                   data-testid="create-test-team-invite-code">
                {result.team.invite_code}
              </div>
            </div>
            <div>
              <div className="text-xs text-neutral-500 mb-1">
                Coach dashboard (single-use magic link — click to sign in as coach)
              </div>
              <a
                href={result.coach_signin_url}
                target="_blank" rel="noreferrer"
                className="block font-mono text-xs bg-neutral-900 rounded px-3 py-2 break-all underline text-emerald-400"
                data-testid="create-test-team-coach-url"
              >{result.coach_signin_url}</a>
            </div>
            <button
              onClick={done}
              className="w-full mt-2 px-3 py-2 rounded bg-neutral-100 text-black text-sm"
              data-testid="create-test-team-done"
            >
              done
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <Field label="team name" v={name} onChange={setName} testid="create-test-team-name" />
            <Field label="coach name" v={coachName} onChange={setCoachName} testid="create-test-team-coach-name" />
            <Field label="coach email" v={coachEmail} onChange={setCoachEmail} testid="create-test-team-coach-email" type="email" />
            <div>
              <label className="label-eyebrow">entity type</label>
              <select
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
                className="w-full mt-1 bg-transparent border-b border-neutral-700 text-sm py-1 focus:outline-none focus:border-neutral-400"
                data-testid="create-test-team-entity-type"
              >
                {["academy", "club", "school", "university", "rep_team", "state", "national"].map((t) => (
                  <option key={t} value={t} className="bg-neutral-950">{t}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="city (optional)" v={city} onChange={setCity} testid="create-test-team-city" />
              <Field label="country (optional)" v={country} onChange={setCountry} testid="create-test-team-country" />
            </div>
            <button
              onClick={create}
              disabled={busy || !name.trim() || !coachName.trim() || !coachEmail.trim()}
              className="w-full px-3 py-2 rounded bg-neutral-100 text-black text-sm disabled:opacity-40"
              data-testid="create-test-team-submit"
            >
              {busy ? "creating…" : "create team + magic link"}
            </button>
            <p className="text-[10px] text-neutral-500">
              Smoke test only. No email is sent. The coach sign-in link is returned inline for immediate testing.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, v, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="label-eyebrow">{label}</label>
      <input
        type={type}
        value={v}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 bg-transparent border-b border-neutral-700 text-sm py-1 focus:outline-none focus:border-neutral-400"
        data-testid={testid}
      />
    </div>
  );
}

export default FounderTeamsSection;
